import { useRoute } from '@nuxtjs/composition-api';
import { SITEMAP_URL, HOST_URL, AMP_URL, TWITTER_USERNAME } from '../../../constants/config';
import { ogMeta } from '../../../constants/site';
import { rssProxies } from '../../../constants/rssProxies';
import { getArticleUrl } from './routingUtils';

/**
 * Get meta tags for open graph
 *
 * @info default değerler için direk çağırın parametreler opsiyonel
 * @example getMetaOpenGraph();
 *
 * @info eğer sadece image değiştirmek istiyorsanız
 * @example getMetaOpenGraph({imageUrl: 'https://yeniurl'})
 *
 * @param {object} config - open graph config
 * @param {string} [config.url] - optional
 * @param {string} [config.title] - optional
 * @param {string} [config.description] - optional
 * @param {string} [config.imageUrl] - optional
 */
export const getMetaOpenGraph = ({ url, title, description, imageUrl } = ogMeta) => {
  return [
    {
      hid: 'og:url',
      property: 'og:url',
      content: url,
    },
    {
      hid: 'og:title',
      property: 'og:title',
      content: title,
    },
    {
      hid: 'og:description',
      property: 'og:description',
      content: description,
    },
    {
      hid: 'og:image',
      property: 'og:image',
      content: imageUrl,
    },
    {
      hid: 'og:image:secure_url',
      property: 'og:image:secure_url',
      content: imageUrl,
    },
  ];
};

/**
 * Get meta tags for twitter
 *
 * @info default değerler için direk çağırın parametreler opsiyonel
 * @example getMetaTwitter();
 *
 * @info eğer sadece image değiştirmek istiyorsanız
 * @example getMetaTwitter({imageUrl: 'https://yeniurl'})
 *
 * @param {object} config - open graph config
 * @param {string} [config.url] - optional
 * @param {string} [config.domain] - optional
 * @param {string} [config.title] - optional
 * @param {string} [config.description] - optional
 * @param {string} [config.imageUrl] - optional
 */
export const getMetaTwitter = ({ url, domain, title, description, imageUrl } = ogMeta) => {
  return [
    {
      hid: 'twitter:url',
      name: 'twitter:url',
      content: url,
    },
    {
      hid: 'twitter:domain',
      name: 'twitter:domain',
      content: domain || ogMeta.domain,
    },
    {
      hid: 'twitter:title',
      name: 'twitter:title',
      content: title,
    },
    {
      hid: 'twitter:description',
      name: 'twitter:description',
      content: description,
    },
    {
      hid: 'twitter:image',
      name: 'twitter:image',
      content: imageUrl,
    },
    {
      hid: 'twitter:card',
      name: 'twitter:card',
      content: 'summary_large_image',
    },
    {
      hid: 'twitter:creator',
      name: 'twitter:creator',
      content: TWITTER_USERNAME,
    },
    {
      hid: 'twitter:site',
      name: 'twitter:site',
      content: TWITTER_USERNAME,
    },
  ];
};

/**
 */
export const getLinkMeta = (article, canonical = false) => {
  if (article) {
    const articleUrlAMP = AMP_URL + getArticleUrl({ article });
    const isArticleTest = article?.flags?.isTest || false;
    const isDraftArticle = article?.flags?.isDraft || false;
    const isHiddenInLists = article?.flags?.isHidden || false;
    const link = [];
    const isAstrologyArticle = article?.route?.startsWith('/astroloji');
    // Insert AMP Url if article is not test
    if (!isArticleTest && !isDraftArticle && !isHiddenInLists && !isAstrologyArticle) {
      link.push({ rel: 'amphtml', href: articleUrlAMP });
    }
    // Insert canonical link if canonical param true
    if (canonical) {
      const articleUrl = HOST_URL + getArticleUrl({ article });
      link.push({ hid: 'canonical', name: 'canonical', rel: 'canonical', href: articleUrl });
    }
    return { link };
  }
  const route = useRoute();
  const path = route.value?.path;
  const link = [{ hid: 'canonical', rel: 'canonical', href: HOST_URL + path }];
  // rss link
  const categorySlug = path?.replace('/', '');
  const categoryRss = rssProxies()
    .find((item) => item.includes(categorySlug))
    ?.replace(SITEMAP_URL, '');
  if (categoryRss) {
    link.push({ rel: 'alternate', type: 'application/rss+xml', title: categorySlug, href: categoryRss });
  }

  return { link };
};
